import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Landmark, Home, ClipboardCheck, ChevronRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const GROUP_CONFIG = {
  foundation_structure: {
    icon: Landmark,
    color: "#2F5D8A",
    bgColor: "#DCE4ED",
    borderColor: "#2F5D8A",
  },
  roof_exterior: {
    icon: Home,
    color: "#6B5B8D",
    bgColor: "#E0DEE8",
    borderColor: "#6B5B8D",
  },
  occupancy_field_rules: {
    icon: ClipboardCheck,
    color: "#4A7A56",
    bgColor: "#D8E8D4",
    borderColor: "#4A7A56",
  },
};

const InspectorTrainingButtons = () => {
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTraining = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inspector-training/categories`);
      setGroups(res.data || []);
    } catch {
      // silently fail - training is optional
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTraining(); }, [fetchTraining]);

  if (loading || groups.length === 0) return null;

  const openGroup = groups.find(g => g.group === activeGroup);
  const config = activeGroup ? GROUP_CONFIG[activeGroup] || GROUP_CONFIG.foundation_structure : null;

  return (
    <>
      {/* Training Buttons Row */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="inspector-training-buttons">
        {groups.map(group => {
          const cfg = GROUP_CONFIG[group.group] || GROUP_CONFIG.foundation_structure;
          const Icon = cfg.icon;
          const imgCount = group.categories.reduce((sum, c) => sum + c.images.length, 0);
          return (
            <button
              key={group.group}
              onClick={() => setActiveGroup(group.group)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border transition-all hover:shadow-sm"
              style={{
                backgroundColor: cfg.bgColor,
                borderColor: `${cfg.borderColor}40`,
                color: cfg.color,
              }}
              data-testid={`training-btn-${group.group}`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span>{group.label}</span>
              <span
                className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold"
                style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }}
              >
                {imgCount}
              </span>
              <ChevronRight className="h-3.5 w-3.5 opacity-50" />
            </button>
          );
        })}
      </div>

      {/* Training Modal */}
      {activeGroup && openGroup && config && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setActiveGroup(null); }}
          data-testid="training-modal"
        >
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
              style={{ borderBottomColor: `${config.borderColor}30` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: config.bgColor }}
                >
                  <config.icon className="h-5 w-5" style={{ color: config.color }} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1F2937]">{openGroup.label}</h2>
                  <p className="text-xs text-[#6B7280]">
                    {openGroup.categories.length} section{openGroup.categories.length !== 1 ? "s" : ""} &middot;{" "}
                    {openGroup.categories.reduce((s, c) => s + c.images.length, 0)} reference images
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveGroup(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="training-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {openGroup.categories.map((cat, idx) => (
                <div key={idx} data-testid={`training-category-${idx}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: config.color }}
                    />
                    <h3
                      className="text-sm font-bold uppercase tracking-wider"
                      style={{ color: config.color }}
                    >
                      {cat.category}
                    </h3>
                    <span className="text-[10px] text-[#9CA3AF] font-medium">
                      ({cat.images.length} image{cat.images.length !== 1 ? "s" : ""})
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {cat.images.map((imgUrl, imgIdx) => (
                      <div
                        key={imgIdx}
                        onClick={() => setLightboxImg(imgUrl)}
                        className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-all hover:shadow-md bg-gray-50"
                        data-testid={`training-image-${idx}-${imgIdx}`}
                      >
                        <img
                          src={imgUrl}
                          alt={`${cat.category} ${imgIdx + 1}`}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold bg-black/50 px-2 py-1 rounded">
                            View Full
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {idx < openGroup.categories.length - 1 && (
                    <div className="border-t border-gray-100 mt-5" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={() => setLightboxImg(null)}
          data-testid="training-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            onClick={() => setLightboxImg(null)}
            data-testid="training-lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxImg}
            alt="Training reference"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default InspectorTrainingButtons;
